import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const separatorImageDef: SlotGroupDefinition = {
  label: 'Separator Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

/**
 * Full-bleed band that visually divides the apartment and community
 * galleries: a coloured panel with a headline on the left and a featured
 * image bleeding off the right edge. Pulled out of the original combined
 * Gallery component so it can be reordered, hidden, or duplicated as its
 * own page section.
 */
export function GallerySeparator() {
  const img = useSlotGroup('galleryseparator.image', separatorImageDef);

  return (
    <section className="bg-white">
      <div className="gallery-separator">
        <div className="gallery-separator__inner">
          {/* Title lives on the left in the coloured band */}
          <div className="gallery-separator__title-box">
            <RichTextContent
              path="galleryseparator.title"
              as="h2"
              className="gallery-separator__title"
            />
          </div>

          {/* Image on the right, pulled slightly left so text overlaps its edge */}
          <div className="gallery-separator__image-outer">
            <EditableSlotGroup
              groupPath="galleryseparator.image"
              definition={separatorImageDef}
            >
              <div className="gallery-separator__image-wrap">
                <VibesitesImage
                  url={img.image || ''}
                  alt={img.alt || ''}
                  className="w-full h-full object-cover"
                />
              </div>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Separator — full viewport width */
        .gallery-separator {
          width: 100%;
          background-color: var(--vibe-color-primary);
          padding: 6.5rem 1.5rem;
        }

        .gallery-separator__inner {
          max-width: 72rem;
          margin-left: auto;
          margin-right: auto;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0;
        }

        /* Left column — text sits on the coloured band */
        .gallery-separator__title-box {
          flex: 0 0 34%;
          position: relative;
          z-index: 2;
          padding-right: 1.5rem;
        }

        .gallery-separator__title {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 3vw, 2.75rem);
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          line-height: 1.15;
          color: var(--vibe-color-text-on-primary);
          margin: 0;
        }

        .gallery-separator__title-accent {
          color: var(--vibe-color-accent);
        }

        /* Right column — image pulled left so text overlaps its edge */
        .gallery-separator__image-outer {
          flex: 1 1 0;
          position: relative;
          z-index: 1;
          margin-left: -6rem;
        }

        .gallery-separator__image-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 7;
          overflow: hidden;
          border-radius: 3px;
        }

        /* very light left-edge gradient so text stays legible where it overlaps */
        .gallery-separator__image-wrap::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to right, rgba(0,0,0,0.25) 0%, transparent 30%);
          pointer-events: none;
        }

        @media (max-width: 767px) {
          .gallery-separator {
            padding: 3.5rem 1.25rem;
          }
          .gallery-separator__inner {
            flex-direction: column;
            gap: 1.25rem;
          }
          .gallery-separator__title-box {
            flex: 0 0 auto;
            width: 100%;
            padding-right: 0;
            z-index: 1;
          }
          .gallery-separator__title {
            text-align: center;
          }
          .gallery-separator__image-outer {
            width: 100%;
            margin-left: 0;
          }
          .gallery-separator__image-wrap {
            aspect-ratio: 4 / 3;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GallerySeparator',
  displayName: 'Gallery Separator',
  tokens: {
    'galleryseparator.title': {
      label: 'Headline',
      type: 'richtext',
      defaultValue:
        '<p>A Life <span class="gallery-separator__title-accent">Elevated</span> to the Highest Expectations</p>',
    },
  },
  slotGroups: {
    'galleryseparator.image': separatorImageDef,
  },
};

(GallerySeparator as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GallerySeparator;
